import { FC, useState } from "react"
import { Field, Formik, FormikErrors } from "formik"
import { FieldProps } from "formik/dist/Field"
import { FormikConfig } from "formik/dist/types"
import { Div } from "trunx"

import { EmailInput, PhoneInput } from "components"
import { Box, Button, Container, Section, Img, Text } from "ui-kit/components-branded"
import { Spinner } from "components/Spinner"
import { parsePhoneNumber } from "utils/phone-number"
import { validationErrText, validationPattern } from "utils/email"
import { httpClient } from "utils/infrastructure/httpClient"

const errorPhoneMessage = "Пожалуйста, введите корректный номер телефона"

interface FormValues {
    email: string
    phone: string
}

const formConfig = {
    email: "",
    phone: "",
}

function validate(values: FormValues) {
    const errors: FormikErrors<FormValues> = {}
    const phoneNumber = parsePhoneNumber(values.phone)

    if (!values.email.length) {
        errors.email = "Введите email"
    }

    if (!new RegExp(validationPattern).test(values.email)) {
        errors.email = validationErrText
    }

    if (!values.phone.length) {
        errors.phone = "Введите номер телефона"
    }

    if (!phoneNumber.isValid) {
        errors.phone = errorPhoneMessage
    } else {
        values.phone = phoneNumber.internationalFormat ?? values.phone
    }

    return errors
}

type Status = "idle" | "loading" | "success" | "error"

interface Props {
    reserved: boolean
}

export const WaitListForm: FC<Props> = (props) => {
    const [status, setStatus] = useState<Status>("idle")
    const errorHandler = () => {
        setStatus("error")
    }
    const successHandler = () => {
        setStatus("success")
    }

    const submitHandler: FormikConfig<FormValues>["onSubmit"] = (values) => {
        setStatus("loading")
        return httpClient.post("client/free/waitlist", values).then(successHandler, errorHandler)
    }

    if (status === "success")
        return (
            <div className="flex-grow-1 w-100">
                <div className="survey-results__heading-container">
                    <div className="redesign-container">
                        <div className="survey-results__heading">
                            <Img src="white_check_mark.png" className="whitelist-form__img" />{" "}
                            <br />
                            Вы&nbsp;добавлены в&nbsp;лист ожидания. <br /> Как только свободные
                            психологи появятся, мы&nbsp;пришлем вам письмо со&nbsp;ссылкой.
                        </div>
                    </div>
                </div>
            </div>
        )

    if (props.reserved) {
        return (
            <div className="flex-grow-1 w-100">
                <div className="survey-results__heading-container">
                    <div className="redesign-container">
                        <div className="survey-results__heading">
                            <Img src="email.png" className="whitelist-form__img" />К сожалению,
                            свободное время психологов закончилось.
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="survey-results__heading-container">
                <div className="redesign-container">
                    <div className="survey-results__heading">
                        <Img src="email.png" className="whitelist-form__img" /> <br />
                        Сейчас все психологи заняты.
                        <br />
                        Оставьте почту, и&nbsp;мы&nbsp;сообщим, когда появятся места для записи.
                        <hr className="whitelist-form__sep" />
                        Этот проект для тех, кто испытывает сильную тревогу и&nbsp;стресс из-за
                        событий 22.03.2024
                    </div>
                </div>
            </div>
            <Container className="whitelist-form__container is-paddingless">
                <Section>
                    <Formik initialValues={formConfig} onSubmit={submitHandler} validate={validate}>
                        <form>
                            <Box className="is-flex flex-column whitelist-form">
                                <Div bulma="block">
                                    <Field name="email">
                                        {({ field, meta }: FieldProps<string, FormValues>) => {
                                            return (
                                                <>
                                                    <EmailInput
                                                        disabled={
                                                            status !== "idle" && status !== "error"
                                                        }
                                                        {...field}
                                                        pattern={undefined}
                                                        required={false}
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <Text className="has-text-danger">
                                                            {meta.error}
                                                        </Text>
                                                    )}
                                                </>
                                            )
                                        }}
                                    </Field>
                                </Div>
                                <Div bulma="block">
                                    <Field name="phone">
                                        {({ field, meta }: FieldProps<string, FormValues>) => {
                                            return (
                                                <>
                                                    <PhoneInput
                                                        disabled={
                                                            status !== "idle" && status !== "error"
                                                        }
                                                        {...field}
                                                        pattern={undefined}
                                                        required={false}
                                                        className="input form-control"
                                                        placeholder="Введите номер телефона"
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <Text className="has-text-danger">
                                                            {meta.error}
                                                        </Text>
                                                    )}
                                                </>
                                            )
                                        }}
                                    </Field>
                                </Div>
                                <Button
                                    color="primary"
                                    disabled={status !== "idle" && status !== "error"}
                                >
                                    Записаться в&nbsp;лист ожидания
                                </Button>
                                {status === "error" && (
                                    <Text className="mgt-2 has-text-danger">
                                        Пользователь с&nbsp;таким email уже бронировал сессию или
                                        уже есть в&nbsp;листе ожидания
                                    </Text>
                                )}
                            </Box>
                        </form>
                    </Formik>
                    {status == "loading" && <Spinner className="mgt-1" />}
                </Section>
            </Container>
        </>
    )
}
